/**
 * TtsService - Accumulates streaming text into sentences, fetches TTS audio
 * from xAI Grok TTS via our server proxy, and plays them sequentially.
 *
 * Uses <audio> elements with blob URLs for playback — no AudioContext needed,
 * no user-gesture restrictions. Sentences are fetched in parallel so only the
 * first sentence has full round-trip latency; subsequent ones pre-fetch while
 * the current one plays.
 */
export const TTS_EVENTS = {
  SPEAKING_STARTED: 'speaking_started',
  SPEAKING_ENDED: 'speaking_ended',
  PAUSED: 'paused',
  RESUMED: 'resumed',
  INTERRUPTED: 'interrupted',
  ERROR: 'error',
  PROGRESS: 'progress',
}

// Mirrors VoiceSpeechController::MAX_TEXT_LENGTH — the server rejects
// anything longer with a 422, which the client treats as a skipped sentence.
const MAX_SYNTHESIZE_CHARS = 4096

// Strips markdown formatting text won't want spoken aloud. Pure (no `this`),
// so it doubles as the source of truth for paragraphBatchEnd's maxChars check
// below — the server sees this stripped text, not the raw buffered text.
export function stripMarkdown(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/\*(.+?)\*/g, '$1') // italic
    .replace(/__(.+?)__/g, '$1') // bold alt
    .replace(/_(.+?)_/g, '$1') // italic alt
    .replace(/~~(.+?)~~/g, '$1') // strikethrough
    .replace(/`(.+?)`/g, '$1') // inline code
    .replace(/```[\s\S]*?```/g, ' code example ') // code blocks
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links - keep text
    .replace(/^#{1,6}\s+/gm, '') // headings
    .replace(/^[-*+]\s+/gm, '') // list markers
    .replace(/^\d+\.\s+/gm, '') // ordered list markers
    .replace(/^>\s+/gm, '') // blockquotes
    .replace(/---+/g, '') // horizontal rules
    .replace(/\n{3,}/g, '\n\n') // collapse extra newlines
    .trim()
}

// Scans buffer for consecutive paragraph breaks (\n\n+) and returns the end
// index of the farthest one that keeps the batch under minChars, extending
// past it by one more paragraph so the batch actually reaches minChars.
// Never grows a batch of 2+ paragraphs past maxChars, measured *after*
// stripMarkdown rather than on the raw buffer — the synthesize endpoint
// hard-rejects oversized requests (VoiceSpeechController::MAX_TEXT_LENGTH)
// based on what's actually sent, and matching that exactly (instead of
// relying on raw length as a proxy) stays correct even if stripMarkdown's
// rules change later. Once another paragraph would cross that ceiling, the
// batch fires early with whatever it already has, even under minChars (a
// single paragraph already over maxChars on its own still goes through
// alone, unchanged from before batching existed — splitting mid-paragraph
// is out of scope here).
// Returns -1 if the currently-buffered paragraphs don't reach minChars yet.
export function paragraphBatchEnd(buffer, minChars, maxChars) {
  const paragraphPattern = /\n\n+/
  let boundaryEnd = -1
  let searchFrom = 0
  let cappedByMax = false
  while (searchFrom < buffer.length) {
    const match = paragraphPattern.exec(buffer.slice(searchFrom))
    if (!match) break
    const candidateEnd = searchFrom + match.index + match[0].length
    if (
      boundaryEnd !== -1 &&
      stripMarkdown(buffer.slice(0, candidateEnd)).length > maxChars
    ) {
      cappedByMax = true
      break
    }
    boundaryEnd = candidateEnd
    if (boundaryEnd >= minChars) break
    searchFrom = boundaryEnd
  }
  return boundaryEnd >= minChars || cappedByMax ? boundaryEnd : -1
}

export class TtsService {
  constructor({ voice = 'eve', playbackRate = 1.0 } = {}) {
    this.voice = voice
    this.playbackRate = playbackRate
    this.buffer = ''
    this.slots = new Map() // index → { url, text } — ordered fetch results
    this.nextFetchIndex = 0
    this.nextPlayIndex = 0
    this.playing = false
    this.paused = false
    this.interrupted = false
    this.hasFiredSpeakingStarted = false
    this.currentAudio = null
    this.currentText = null
    this.currentUrl = null
    this.epoch = 0
    this.listeners = new Map()
    this.drainTimer = null
    this.flushed = false
    this.failedSlots = new Set()
    this.hasCompletedPlayback = false
    // Text-length-weighted progress across chunks, since playback is a
    // sequence of separately-fetched <audio> elements rather than one — see
    // #emitProgress.
    this.totalTextLength = 0
    this.playedTextLength = 0
    this.chunkLengths = new Map() // index → cleaned text length, for #advancePastFailures
  }

  // -- Public API --

  addText(chunk) {
    this.interrupted = false
    this.buffer += chunk
    this.#drainParagraphs()
    this.#scheduleBufferFlush()
  }

  flush() {
    this.#clearDrainTimer()
    this.flushed = true
    const text = this.buffer.trim()
    this.buffer = ''
    if (text.length > 0) {
      this.#enqueue(text)
    } else {
      this.#checkComplete()
    }
  }

  interrupt() {
    this.interrupted = true
    // Bump the epoch so any fetch already in flight from before this
    // interrupt is recognized as stale even after addText() below resets
    // `interrupted` back to false for the request that replaces it.
    this.epoch++
    this.#hardReset()
    this.#emit(TTS_EVENTS.INTERRUPTED)
  }

  // Stop current audio and clear all queued state. Used by interrupt() and
  // setVoice() — setVoice repopulates the queue afterward, interrupt doesn't.
  #hardReset() {
    this.hasFiredSpeakingStarted = false
    this.#clearDrainTimer()
    this.buffer = ''
    this.flushed = false
    this.slots.forEach(({ url }) => URL.revokeObjectURL(url))
    this.slots = new Map()
    this.failedSlots = new Set()
    this.hasCompletedPlayback = false
    this.nextFetchIndex = 0
    this.nextPlayIndex = 0
    this.totalTextLength = 0
    this.playedTextLength = 0
    this.chunkLengths = new Map()

    if (this.currentAudio) {
      // Detach handlers first — clearing src fires onerror, which would
      // otherwise propagate as a playback failure.
      this.currentAudio.onended = null
      this.currentAudio.onerror = null
      this.currentAudio.pause()
      this.currentAudio.src = ''
      this.currentAudio = null
    }
    if (this.currentUrl) {
      URL.revokeObjectURL(this.currentUrl)
      this.currentUrl = null
    }
    this.currentText = null

    this.playing = false
    this.paused = false
  }

  // Pause current playback (also between chunks) without throwing away the queue.
  // resume() picks up from there. interrupt() is the destructive version.
  pause() {
    if (!this.hasFiredSpeakingStarted || this.interrupted || this.paused) return
    this.currentAudio?.pause()
    this.paused = true
    this.#emit(TTS_EVENTS.PAUSED)
  }

  resume() {
    if (!this.paused) return
    this.paused = false
    if (this.currentAudio) {
      const audio = this.currentAudio
      const text = this.currentText
      this.currentAudio.play().catch((e) => {
        if (this.currentAudio !== audio || this.interrupted) return
        URL.revokeObjectURL(this.currentUrl)
        this.currentAudio = null
        this.currentText = null
        this.currentUrl = null
        this.playing = false
        this.playedTextLength += text?.length ?? 0
        this.#emitProgress()
        console.warn('TTS: resume play() rejected, skipping sentence:', e)
        this.#advancePastFailures()
      })
    } else if (this.slots.has(this.nextPlayIndex)) {
      this.#playNext()
    }
    this.#emit(TTS_EVENTS.RESUMED)
  }

  setVoice(voice) {
    if (this.voice === voice) return
    this.voice = voice

    // Re-fetch the currently-playing + all queued sentences in the new voice.
    // It's the smallest unit we can reliably re-render mid-playback.
    const pendingTexts = []
    if (this.currentText) pendingTexts.push(this.currentText)
    const sortedKeys = [...this.slots.keys()].sort((a, b) => a - b)
    for (const key of sortedKeys) pendingTexts.push(this.slots.get(key).text)
    const remainingBuffer = this.buffer
    const wasPaused = this.paused
    const wasFlushed = this.flushed

    this.epoch++
    this.#hardReset()
    this.paused = wasPaused

    for (const text of pendingTexts) this.#enqueue(text)
    if (remainingBuffer.trim()) this.buffer = remainingBuffer
    if (wasFlushed) this.flush()
  }

  setPlaybackRate(rate) {
    this.playbackRate = rate
    if (this.currentAudio) this.currentAudio.playbackRate = rate
  }

  destroy() {
    this.interrupt()
    this.listeners.clear()
  }

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set())
    this.listeners.get(event).add(callback)
  }

  off(event, callback) {
    this.listeners.get(event)?.delete(callback)
  }

  get isSpeaking() {
    return this.playing
  }

  get isPaused() {
    return this.paused
  }

  // -- Private --

  #emit(event, data = null) {
    this.listeners.get(event)?.forEach((cb) => {
      try {
        cb(data)
      } catch (e) {
        console.error(`TtsService event error (${event}):`, e)
      }
    })
  }

  // Cumulative fraction (0-1) of total message text "passed", weighted by
  // text length rather than audio duration since each chunk is a separately
  // fetched <audio> element — binding to just the current chunk's
  // currentTime/duration would make the bar reset at every chunk boundary.
  #emitProgress() {
    if (this.totalTextLength === 0) return
    const audio = this.currentAudio
    const currentFraction =
      audio && Number.isFinite(audio.duration) && audio.duration > 0
        ? audio.currentTime / audio.duration
        : 0
    const currentLength = this.currentText ? this.currentText.length : 0
    const fraction = Math.min(
      1,
      (this.playedTextLength + currentFraction * currentLength) /
        this.totalTextLength
    )
    this.#emit(TTS_EVENTS.PROGRESS, fraction)
  }

  #drainParagraphs() {
    // Batch consecutive paragraphs up to the same size floor #scheduleBufferFlush
    // uses below, instead of firing one request per paragraph break. Each
    // request is an independent xAI TTS generation with no cross-call voice
    // consistency guarantee, so many small paragraph-sized requests produce
    // audible pitch/timbre seams between clips.
    for (;;) {
      const firstChunk = this.nextFetchIndex === 0
      const minChars = firstChunk ? 120 : 1500
      const boundaryEnd = paragraphBatchEnd(
        this.buffer,
        minChars,
        MAX_SYNTHESIZE_CHARS
      )
      if (boundaryEnd === -1) return

      const batch = this.buffer.slice(0, boundaryEnd).trim()
      this.buffer = this.buffer.slice(boundaryEnd)
      if (batch.length > 0) this.#enqueue(batch)
    }
  }

  // Flush buffer on a timer when no paragraph break arrives.
  // First chunk uses a low threshold (120 chars / 200ms) to start audio fast.
  // Subsequent chunks wait for much more text (1500 chars / 400ms) so single
  // long replies still produce only a handful of synthesize requests.
  #scheduleBufferFlush() {
    this.#clearDrainTimer()
    const firstChunk = this.nextFetchIndex === 0
    const minChars = firstChunk ? 120 : 1500
    const delayMs = firstChunk ? 200 : 400

    if (this.buffer.trim().length < minChars) return

    this.drainTimer = setTimeout(() => {
      this.drainTimer = null
      const text = this.buffer.trim()
      this.buffer = ''
      if (text.length > 0) this.#enqueue(text)
    }, delayMs)
  }

  #clearDrainTimer() {
    if (this.drainTimer) {
      clearTimeout(this.drainTimer)
      this.drainTimer = null
    }
  }

  #checkComplete() {
    if (!this.flushed || this.playing || this.interrupted) return
    for (let i = this.nextPlayIndex; i < this.nextFetchIndex; i++) {
      if (!this.failedSlots.has(i)) return
    }

    const anyPlayed = this.hasCompletedPlayback
    this.hasFiredSpeakingStarted = false
    this.hasCompletedPlayback = false

    if (!anyPlayed && this.nextFetchIndex > 0) {
      this.#emit(TTS_EVENTS.ERROR, new Error('All TTS sentences failed'))
    } else {
      this.#emit(TTS_EVENTS.SPEAKING_ENDED)
    }
  }

  // Advances nextPlayIndex past any already-failed chunks, in strict index
  // order — the only place playedTextLength credits a failed chunk. Chunks
  // fetch in parallel and can fail in any order, so crediting a failure the
  // moment it happens (rather than when playback order actually reaches it)
  // would let a later chunk jump the progress bar ahead of an earlier chunk
  // that's still fetching or playing.
  #advancePastFailures() {
    let advanced = false
    while (
      this.nextPlayIndex < this.nextFetchIndex &&
      this.failedSlots.has(this.nextPlayIndex)
    ) {
      this.playedTextLength += this.chunkLengths.get(this.nextPlayIndex) ?? 0
      this.nextPlayIndex++
      advanced = true
    }
    if (advanced) this.#emitProgress()

    if (this.slots.has(this.nextPlayIndex)) {
      this.#playNext()
    } else if (!this.playing) {
      this.#checkComplete()
    }
  }

  #enqueue(text) {
    const cleaned = stripMarkdown(text)
    if (cleaned.length === 0) return
    const index = this.nextFetchIndex++
    this.totalTextLength += cleaned.length
    this.chunkLengths.set(index, cleaned.length)
    this.#fetchAndQueue(cleaned, index)
  }

  async #fetchAndQueue(text, index) {
    const epoch = this.epoch
    try {
      const response = await fetch('/voice_speech/synthesize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content'),
        },
        credentials: 'same-origin',
        body: JSON.stringify({ text, voice: this.voice }),
      })

      if (!response.ok)
        throw new Error(`TTS request failed: ${response.status}`)
      if (this.interrupted || this.epoch !== epoch) return

      const blob = await response.blob()
      if (this.interrupted || this.epoch !== epoch) return

      const url = URL.createObjectURL(blob)
      this.slots.set(index, { url, text })
      this.#playNext()
    } catch (error) {
      if (this.interrupted || this.epoch !== epoch) return
      console.warn(`TTS: sentence ${index} failed, skipping:`, error)
      this.failedSlots.add(index)
      this.#advancePastFailures()
    }
  }

  #playNext() {
    if (this.playing || this.interrupted || this.paused) return
    if (!this.slots.has(this.nextPlayIndex)) return

    this.playing = true

    if (!this.hasFiredSpeakingStarted) {
      this.hasFiredSpeakingStarted = true
      this.#emit(TTS_EVENTS.SPEAKING_STARTED)
    }

    const { url, text } = this.slots.get(this.nextPlayIndex)
    this.slots.delete(this.nextPlayIndex)
    this.nextPlayIndex++

    const audio = new Audio(url)
    audio.playbackRate = this.playbackRate
    this.currentAudio = audio
    this.currentText = text
    this.currentUrl = url

    this.currentAudio.onended = () => {
      URL.revokeObjectURL(url)
      this.currentAudio = null
      this.currentText = null
      this.currentUrl = null
      this.playing = false
      this.hasCompletedPlayback = true
      this.playedTextLength += text.length
      this.#emitProgress()

      if (this.interrupted) return
      this.#advancePastFailures()
    }

    this.currentAudio.ontimeupdate = () => this.#emitProgress()

    this.currentAudio.onerror = (e) => {
      if (this.currentAudio !== audio || this.interrupted) return
      URL.revokeObjectURL(url)
      this.currentAudio = null
      this.currentText = null
      this.currentUrl = null
      this.playing = false
      this.playedTextLength += text.length
      this.#emitProgress()
      console.warn('TTS: audio decode failed, skipping sentence:', e)
      this.#advancePastFailures()
    }

    audio.play().catch((e) => {
      if (this.currentAudio !== audio || this.interrupted) return
      URL.revokeObjectURL(this.currentUrl)
      this.currentAudio = null
      this.currentText = null
      this.currentUrl = null
      this.playing = false
      this.playedTextLength += text.length
      this.#emitProgress()
      console.warn('TTS: audio.play() rejected, skipping sentence:', e)
      this.#advancePastFailures()
    })
  }
}
